/**
 * ForwardModal Component
 * Modal for selecting conversations to forward a message to.
 * Supports multi-selection with tabs for Chats and Rooms.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useForwarding } from '../../context/ForwardingContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useRoomList } from '../../hooks/useRoomList';
import { useChannels } from '../../hooks/useChannels';
import { useSortedConversations } from '../../hooks/useSorting';
import { canSendMessages } from '../../utils/roomUtils';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import logger from '../../utils/logger';
import SelectableConversationList from '../SelectableConversationList';
import './ForwardModal.css';

const ForwardModal = ({ onClose }) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { forwardToRooms } = useForwarding();
  const { showError, dismissAll } = useToast();
  const { roomList, isLoading: isLoadingChats } = useRoomList();
  const { channelList, isLoading: isLoadingRooms } = useChannels();

  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [activeTab, setActiveTab] = useState('chats');
  const [searchTerm, setSearchTerm] = useState('');
  const [isForwarding, setIsForwarding] = useState(false);
  const [showLimitError, setShowLimitError] = useState(false);
  const [shakeCounter, setShakeCounter] = useState(false);

  const MAX_FORWARD_ROOMS = 5;

  // Refs to track timeout IDs for clearing
  const shakeTimeoutRef = useRef(null);
  const errorTimeoutRef = useRef(null);

  // Shared filter function for rooms and chats
  const filterRoomList = useCallback((list) => {
    return (list || []).filter(r => {
      const room = client.getRoom(r.id);
      if (!room || room?.getMyMembership() === 'invite') return false;
      return true;
    }).map(r => {
      const room = client.getRoom(r.id);
      const disabled = !canSendMessages(room, client);
      return { ...r, disabled, disabledReason: disabled ? t('ForwardModal.noSendPermission') : null };
    });
  }, [client, t]);

  // Filter for only joined rooms (exclude invites and Server Notices)
  const chats = useSortedConversations(filterRoomList(roomList));
  const rooms = useSortedConversations(filterRoomList(channelList));

  // Get current list based on active tab
  const currentList = activeTab === 'chats' ? chats : rooms;

  // Filter by search term
  const filteredList = currentList.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    // Capture previous overflow value before modifying
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore previous value instead of 'unset'
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // Handle overlay click to close
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Toggle selection of a room
  const handleToggleSelection = useCallback((roomId) => {
    setSelectedRoomIds(prev => {
      if (prev.includes(roomId)) {
        // Deselecting - always allow
        setShowLimitError(false);
        return prev.filter(id => id !== roomId);
      } else {
        // Selecting - check limit
        if (prev.length >= MAX_FORWARD_ROOMS) {
          // Hit the limit - trigger animation and show error
          // Clear any existing timeouts to reset the timer
          if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
          if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
          
          setShakeCounter(true);
          setShowLimitError(true);
          
          shakeTimeoutRef.current = setTimeout(() => setShakeCounter(false), 500);
          errorTimeoutRef.current = setTimeout(() => setShowLimitError(false), 3000);
          
          return prev; // Don't add
        }
        setShowLimitError(false);
        return [...prev, roomId];
      }
    });
  }, []);

  // Handle forward button click
  const handleForward = useCallback(async () => {
    if (selectedRoomIds.length === 0 || isForwarding) {
      return;
    }

    setIsForwarding(true);

    try {
      const results = await forwardToRooms(selectedRoomIds, client);

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (failCount > 0) {
        logger.error('[ForwardModal] Some forwards failed', { failCount });
        // Show error toast to user
        showError(
          t('ForwardModal.errorTitle', 'Forward Failed'),
          t('ForwardModal.errorMessage', `Failed to forward message to ${failCount} conversation(s). Please try again.`),
        );
      }

      if (successCount > 0 && failCount === 0) {
        // Only close modal if all forwards succeeded
        onClose();
      }
    } catch (error) {
      logger.error('[ForwardModal] Forward failed:', error);
      // Show error toast for unexpected errors
      showError(
        t('ForwardModal.errorTitle', 'Forward Failed'),
        t('ForwardModal.networkError', 'Unable to forward message. Please check your connection and try again.'),
      );
    } finally {
      setIsForwarding(false);
    }
  }, [selectedRoomIds, isForwarding, forwardToRooms, client, onClose, showError, t]);

  // Handle cancel button click - dismiss any error toasts and close modal
  const handleCancel = useCallback(() => {
    dismissAll();
    onClose();
  }, [dismissAll, onClose]);

  // Handle clear all selections
  const handleClearAll = useCallback(() => {
    setSelectedRoomIds([]);
    setShowLimitError(false);
  }, []);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const isLoading = activeTab === 'chats' ? isLoadingChats : isLoadingRooms;

  return createPortal(
    <div className="forward-modal-overlay" onClick={handleOverlayClick}>
      <div className="forward-modal">
        {/* Header */}
        <div className="forward-modal-header">
          <h2 className="forward-modal-title">
            {t('ForwardModal.title', 'Forward Message')}
          </h2>
          <button
            className="forward-modal-close"
            onClick={onClose}
            type="button"
            aria-label={t('ForwardModal.close', 'Close')}
          >
            <ion-icon name="close-outline" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="forward-modal-search">
          <div className="forward-modal-search-container">
            <i className="fa fa-search forward-modal-search-icon" />
            <input
              type="text"
              className="forward-modal-search-input"
              placeholder={t('ForwardModal.searchPlaceholder', 'Search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="forward-modal-search-clear"
                onClick={handleClearSearch}
                type="button"
                aria-label={t('ForwardModal.clearSearch', 'Clear search')}
              >
                <ion-icon name="close-outline" />
              </button>
            )}
          </div>
        </div>

        {/* Limit Error Message */}
        {showLimitError && (
          <div className="forward-modal-limit-error">
            {t('ForwardModal.limitError', { maxForwardRooms: MAX_FORWARD_ROOMS })}
          </div>
        )}

        {/* Tabs */}
        <div className="forward-modal-tabs">
          <div className="forward-modal-tabs-left">
            <button
              className={`forward-modal-tab ${activeTab === 'chats' ? 'active' : ''}`}
              onClick={() => setActiveTab('chats')}
              type="button"
            >
              {t('ForwardModal.chatsTab', 'Chats')}
            </button>
            <button
              className={`forward-modal-tab ${activeTab === 'rooms' ? 'active' : ''}`}
              onClick={() => setActiveTab('rooms')}
              type="button"
            >
              {t('ForwardModal.roomsTab', 'Rooms')}
            </button>
          </div>
          <div className="forward-modal-tabs-right">
            <span className={`forward-modal-tabs-counter ${shakeCounter ? 'shake' : ''} ${selectedRoomIds.length >= MAX_FORWARD_ROOMS ? 'limit-reached' : ''}`}>
              {selectedRoomIds.length} {t('ForwardModal.selected', 'Selected')}
            </span>
            {selectedRoomIds.length > 0 && (
              <button
                className="forward-modal-clear-all"
                onClick={handleClearAll}
                type="button"
                aria-label={t('ForwardModal.clearAll', 'Clear all selections')}
              >
                {t('ForwardModal.clearAll', 'Clear All')}
              </button>
            )}
          </div>
        </div>

        {/* Conversation List */}
        <div className="forward-modal-list">
          {isLoading ? (
            <div className="forward-modal-loading">
              <span>{t('ForwardModal.loading', 'Loading...')}</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="forward-modal-empty">
              <span>
                {searchTerm
                  ? t('ForwardModal.noResults', 'No conversations found')
                  : t('ForwardModal.empty', 'No conversations')}
              </span>
            </div>
          ) : (
            <SelectableConversationList
              conversations={filteredList}
              selectedIds={selectedRoomIds}
              onToggleSelection={handleToggleSelection}
              disabledIds={filteredList.filter(c => c.disabled).map(c => c.id || c.roomId)}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="forward-modal-actions">
          <button
            className="forward-modal-btn forward-modal-btn-cancel"
            onClick={handleCancel}
            type="button"
            disabled={isForwarding}
          >
            {t('ForwardModal.cancel', 'Cancel')}
          </button>
          <button
            className="forward-modal-btn forward-modal-btn-forward"
            onClick={handleForward}
            type="button"
            disabled={selectedRoomIds.length === 0 || isForwarding}
          >
            {isForwarding
              ? t('ForwardModal.forwarding', 'Forwarding...')
              : t('ForwardModal.forward', 'Forward')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

ForwardModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ForwardModal;
